import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { OpportunityInactivityReason } from '@inversor-hub/types';
import { OPPORTUNITY_INACTIVITY_REASON_LABELS } from '@/features/opportunities/utils.ts';

interface InactivityReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: OpportunityInactivityReason, reasonOther?: string) => void;
}

export const InactivityReasonDialog: React.FC<InactivityReasonDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [selectedReason, setSelectedReason] = useState<OpportunityInactivityReason | ''>('');
  const [otherReasonText, setOtherReasonText] = useState('');

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as OpportunityInactivityReason;
    setSelectedReason(value);
    if (value !== 'OTHER') {
      setOtherReasonText('');
    }
  };

  const handleConfirm = () => {
    if (!selectedReason) return;

    onConfirm(selectedReason, selectedReason === 'OTHER' ? otherReasonText : undefined);

    // Reset state
    setSelectedReason('');
    setOtherReasonText('');
  };

  const handleClose = () => {
    setSelectedReason('');
    setOtherReasonText('');
    onClose();
  };

  const isFormValid = selectedReason && (selectedReason !== 'OTHER' || otherReasonText.trim());

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Razón para desactivar la oportunidad
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Selecciona la razón por la cual estás desactivando esta oportunidad:
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={selectedReason} onChange={handleReasonChange} sx={{ gap: 1 }}>
              {Object.entries(OPPORTUNITY_INACTIVITY_REASON_LABELS).map(([value, label]) => (
                <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
              ))}
            </RadioGroup>
          </FormControl>

          {selectedReason === 'OTHER' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Especifica el motivo"
              value={otherReasonText}
              onChange={(e) => setOtherReasonText(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Describe el motivo por el cual desactivas la oportunidad..."
              required
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!isFormValid} color="warning">
          Desactivar oportunidad
        </Button>
      </DialogActions>
    </Dialog>
  );
};
